/**
 * Utility functions for aircraft tail number validation and detection
 */

/**
 * Known aircraft registration prefixes by country/region
 * Source: ICAO Aircraft Registration Prefixes
 * https://www.avcodes.co.uk/regprefixcur.asp
 */
const REGISTRATION_PATTERNS = [
  // USA - N-numbers: N + 1-5 alphanumeric, first char after N must be digit
  /^N[1-9][0-9A-Z]{0,4}$/i,

  // Canada: C- + 4 letters (C-GABC, C-FXYZ)
  /^C-[A-Z]{4}$/i,

  // Mexico: XA/XB/XC- + 3 letters
  /^X[ABC]-[A-Z]{3}$/i,

  // UK: G- + 4 alphanumeric
  /^G-[A-Z]{4}$/i,

  // Australia: VH- + 3 letters
  /^VH-[A-Z]{3}$/i,

  // New Zealand: ZK/ZL- + 3 letters
  /^Z[KL]-[A-Z]{3}$/i,

  // Germany: D- + 4 alphanumeric
  /^D-[A-Z]{4}$/i,

  // France: F- + 4 alphanumeric
  /^F-[A-Z]{4}$/i,

  // Switzerland: HB- + 3-4 alphanumeric
  /^HB-[A-Z]{3,4}$/i,

  // Brazil: PP/PR/PS/PT/PU- + 3 letters
  /^P[PRSTU]-[A-Z]{3}$/i,

  // Japan: JA + 4 digits or JA + 4 alphanumeric
  /^JA[0-9A-Z]{4}$/i,

  // South Korea: HL + 4 digits
  /^HL[0-9]{4}$/i,

  // China/Taiwan/HK: B- + 4-5 digits or B-H/K/L/M + 3 alphanumeric
  /^B-[0-9]{4,5}$/i,
  /^B-[HKLM][A-Z]{2}$/i,

  // India: VT- + 3 letters
  /^VT-[A-Z]{3}$/i,

  // UAE: A6- + 3 alphanumeric
  /^A6-[A-Z]{3}$/i,

  // Qatar: A7- + 3 alphanumeric
  /^A7-[A-Z]{3}$/i,

  // Saudi Arabia: HZ- + 3-4 alphanumeric
  /^HZ-[A-Z]{3,4}$/i,

  // South Africa: ZS/ZT/ZU- + 3 letters
  /^Z[STU]-[A-Z]{3}$/i,

  // Ireland: EI/EJ- + 3-4 alphanumeric
  /^E[IJ]-[A-Z]{3,4}$/i,

  // Spain: EC- + 3 letters
  /^EC-[A-Z]{3}$/i,

  // Italy: I- + 4 alphanumeric
  /^I-[A-Z]{4}$/i,

  // Netherlands: PH- + 3-4 alphanumeric
  /^PH-[A-Z]{3,4}$/i,

  // Belgium: OO- + 3 letters
  /^OO-[A-Z]{3}$/i,

  // Austria: OE- + 3-4 alphanumeric
  /^OE-[A-Z]{3,4}$/i,

  // Sweden: SE- + 3 letters
  /^SE-[A-Z]{3}$/i,

  // Norway: LN- + 3 letters
  /^LN-[A-Z]{3}$/i,

  // Denmark: OY- + 3 letters
  /^OY-[A-Z]{3}$/i,

  // Russia: RA- + 5 digits
  /^RA-[0-9]{5}$/i,

  // Turkey: TC- + 3 letters
  /^TC-[A-Z]{3}$/i,

  // Singapore: 9V- + 3 alphanumeric
  /^9V-[A-Z]{3}$/i,

  // Malaysia: 9M- + 3-4 alphanumeric
  /^9M-[A-Z]{3,4}$/i,

  // Thailand: HS- + 3 alphanumeric
  /^HS-[A-Z]{3}$/i,

  // Indonesia: PK- + 3 letters
  /^PK-[A-Z]{3}$/i,

  // Philippines: RP- + 4 digits or RP-C + 4 digits
  /^RP-?C?[0-9]{4}$/i,

  // Argentina: LV/LQ- + 3 letters
  /^L[VQ]-[A-Z]{3}$/i,

  // Colombia: HK- + 4 digits
  /^HK-[0-9]{4}$/i,

  // Bahamas: C6- + 3 letters
  /^C6-[A-Z]{3}$/i,

  // Cayman Islands: VP-C + 2 letters
  /^VP-C[A-Z]{2}$/i,

  // Bermuda: VP-B + 2 letters
  /^VP-B[A-Z]{2}$/i,

  // Isle of Man: M- + 4 alphanumeric
  /^M-[A-Z]{4}$/i,

  // Generic European pattern: 2 letters + dash + 3-4 alphanumeric
  /^[A-Z]{2}-[A-Z0-9]{3,4}$/i,

  // Generic pattern: 1-2 chars + digit(s) + dash + alphanumeric
  /^[A-Z0-9]{1,2}-[A-Z0-9]{3,5}$/i,
];

/**
 * Known placeholder/fake tail number patterns
 */
const PLACEHOLDER_PATTERNS = [
  // Pure numbers (001, 123, 999)
  /^[0-9]+$/,

  // Known placeholder words
  /^(TBD|QUOTE|TEST|DEMO|TEMP|NEW|UNKNOWN|PENDING|NA|N\/A|NONE)$/i,

  // Single characters
  /^.$/,

  // Two characters that don't look like N-numbers (AB, XY, 12)
  // Note: N1-N9 are valid FAA registrations, so we exclude N+digit
  /^(?!N[1-9])..$/,

  // Three characters without dash (likely placeholder, but not N+2 digits)
  // N12, N99 are valid N-numbers
  /^(?!N[1-9][0-9A-Z])[A-Z0-9]{3}$/i,
];

/**
 * Determines if a tail number looks like a real aircraft registration
 * vs a placeholder/quote number.
 *
 * @param tailNumber - The tail number to check
 * @returns true if it looks like a real registration, false if it looks like a placeholder
 */
export function looksLikeRealTail(tailNumber: string): boolean {
  if (!tailNumber || tailNumber.trim().length === 0) {
    return false;
  }

  const normalized = tailNumber.trim().toUpperCase();

  // Check if it matches known placeholder patterns first
  for (const pattern of PLACEHOLDER_PATTERNS) {
    if (pattern.test(normalized)) {
      return false;
    }
  }

  // Check if it matches known registration patterns
  for (const pattern of REGISTRATION_PATTERNS) {
    if (pattern.test(normalized)) {
      return true;
    }
  }

  // Heuristic fallback: if it has a dash and is 5+ chars, probably real
  if (normalized.includes("-") && normalized.length >= 5) {
    return true;
  }

  // Heuristic: starts with N + digit 1-9 (lenient N-number check)
  // FAA N-numbers must have first digit 1-9, not 0
  if (/^N[1-9]/.test(normalized) && normalized.length >= 2) {
    return true;
  }

  // Default: if we can't tell, assume it's a placeholder to be safe
  // (better to ask user to confirm real tail than pollute FAA database)
  return false;
}
